import { useCallback, useRef, useState } from "react";
import { ImageIcon, Mic, Upload } from "lucide-react";

/**
 * Área de upload consistente na recuperação de PIX (modais etapa / modelo).
 */
export function RecoveryFileDropzone({
  accept,
  onFile,
  filename,
  variant = "image",
  className = "",
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const Icon = variant === "audio" ? Mic : ImageIcon;

  const handleFile = useCallback(
    (file) => {
      if (!file || disabled) return;
      onFile(file);
      if (inputRef.current) inputRef.current.value = "";
    },
    [disabled, onFile],
  );

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const title = variant === "audio" ? "Áudio" : "Imagem";
  const hint =
    variant === "audio" ?
      "Arraste o arquivo ou clique para escolher (MP3, OGG, etc.)"
    : "Arraste a imagem ou clique para escolher (JPG, PNG, WebP…)";

  return (
    <div className={className}>
      <input ref={inputRef} type="file" accept={accept} disabled={disabled} className="sr-only" onChange={onInputChange} />
      <button
        type="button"
        disabled={disabled}
        onClick={openPicker}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`group relative flex w-full flex-col items-center justify-center gap-2.5 rounded-xl border-2 border-dashed px-4 py-9 text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/45 disabled:cursor-not-allowed disabled:opacity-50 ${
          dragOver ?
            "border-emerald-400/60 bg-emerald-500/[0.12] shadow-[inset_0_0_0_1px_rgba(16,185,129,0.15)]"
          : "border-white/[0.14] bg-gradient-to-b from-white/[0.04] to-transparent hover:border-emerald-500/35 hover:bg-emerald-500/[0.06]"
        }`}
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300/95 ring-1 ring-emerald-500/20 transition group-hover:bg-emerald-500/25 group-hover:text-emerald-200">
          <Icon className="h-6 w-6" strokeWidth={1.75} />
        </span>
        <span className="text-xs font-semibold text-slate-200">{title}</span>
        <span className="max-w-[260px] text-[11px] leading-relaxed text-slate-500">{hint}</span>
        <span className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-white/[0.1] bg-black/20 px-3 py-1.5 text-[11px] font-medium text-slate-400 transition group-hover:border-emerald-500/25 group-hover:text-emerald-200/90">
          <Upload className="h-3.5 w-3.5 opacity-80" aria-hidden />
          Escolher arquivo
        </span>
      </button>
      {filename ?
        <p className="mt-2 truncate text-center text-[11px] text-slate-500" title={filename}>
          <span className="font-medium text-slate-400">Selecionado:</span> {filename}
        </p>
      : null}
    </div>
  );
}
